import type { StoredMediaFile } from "@repo/types";
import type { TCancelDelivery } from "@schemas/DeliveryForm/CancelDeliverySchema";

import type {
  DeliveryStatus as ApiDeliveryStatus,
  DeliveryType as ApiDeliveryType,
} from "@/libs/generated/types";

import type { Coords } from "../components/map";
import type { Address } from "./address.types";
import type Admin from "./admin.types";
import type { Client } from "./client.types";
import type { DeliveryPayment } from "./deliveryPayment.types";
import type Driver from "./driver.types";
import type { Organization } from "./Organization.types";
import type { Offer } from "./offer.types";
import type { Subscription } from "./subscription.types";
import type { Vehicle } from "./vehicle.types";

export const DeliveryType = {
  STANDARD: "STANDARD",
  TO_RETURN: "TO_RETURN",
  TWO_WAY_DELIVERY: "TWO_WAY_DELIVERY",
  OKM: "OKM",
  QUALITY_CHECK: "QUALITY_CHECK",
} as const satisfies Record<string, ApiDeliveryType>;

export type DeliveryType = ApiDeliveryType;
export type DeliveriesCount = {
  requestedCount?: number;
  confirmedCount?: number;
  onGoingCount?: number;
  waitingDriverCount?: number;
  setCount?: number;
  alertCount?: number;
  deliveredCount: number;
  cancelledCount?: number;
};

export enum DeliveryProgress {
  DELIVERED,
  PENDING,
  IN_PROGRESS,
}

export enum AdminDeliveryStats {
  ByStatus,
  ByType,
}

export enum TwoWayDelivery {
  TO_RETURN = "TO_RETURN",
  EXCHANGE = "EXCHANGE",
}

type LegacyDeliveryStatus = "SMS_SENT" | "CANCELLED";
export type DeliveryStatus = ApiDeliveryStatus | LegacyDeliveryStatus;

export const DeliveryStatus = {
  REQUESTED: "REQUESTED",
  PENDING_CUSTOMER_CONFIRMATION: "PENDING_CUSTOMER_CONFIRMATION",
  CONFIRMED_BY_CUSTOMER: "CONFIRMED_BY_CUSTOMER",
  CANCELLED_BY_CUSTOMER: "CANCELLED_BY_CUSTOMER",
  RESCHEDULED_BY_ADMIN: "RESCHEDULED_BY_ADMIN",
  CANCELLED_BY_ADMIN: "CANCELLED_BY_ADMIN",
  CONFIRMED: "CONFIRMED",
  WAITING_FOR_DRIVER: "WAITING_FOR_DRIVER",
  CANCELLED_BY_MANDATORY: "CANCELLED_BY_MANDATORY",
  SET: "SET",
  ALERT: "ALERT",
  PICKUP_DRIVER: "PICKUP_DRIVER",
  PICKUP_CLIENT: "PICKUP_CLIENT",
  ONGOING: "ONGOING",
  DELIVERED: "DELIVERED",
  SMS_SENT: "SMS_SENT",
  CANCELLED: "CANCELLED",
  SWAP_PICKUP_DRIVER: "SWAP_PICKUP_DRIVER",
  SWAP_IN_TRANSIT: "SWAP_IN_TRANSIT",
  SWAP_PICKUP_CLIENT: "SWAP_PICKUP_CLIENT",
  SWAP_DELIVERED: "SWAP_DELIVERED",
  COMPLETED: "COMPLETED",
} as const satisfies Record<string, DeliveryStatus>;

export type DeliveryFormSectionProps = {
  handleInputChange: <K extends keyof Delivery>(changes: Delivery[K], identifier: K) => void;
  formState: Delivery;
  disabled: boolean;
  errorFields: Array<keyof Delivery>;
};
export type SelectedOption = {
  id: string;
  name: string;
  price: number;
  file?: string;
  invoicePrice?: string;
  input?: string;
};
export type DeliveryOptions = {
  selected: SelectedOption[];
};

export type ClientDashboardMonthDeliveryData = {
  totalPrice: number;
  totalDistance: number;
  totalDeliveries: number;
  averagePrice: number;
  averageDistance: number;
};

export type { CarType } from "@/libs/generated/types";

export type DeliveryLog = {
  id: string;
  log: string;
  createdAt: Date;
  deliveryId?: string;
};
export type DeliveryOptionSelection = {
  id: number;
  optionId: number;
  name?: string;
  clientPrice?: number | null;
  driverReportedAmount?: number | null;
  files?: string | null;
  input?: string | null;
  requireInput?: boolean;
  requiresFile?: boolean;
  requiresMultipleFiles?: boolean;
  fileType?: string | null;
};
export type Delivery = {
  id: number;
  remainingHoursToEstimatedDate?: number; // Resolve field
  totalHoursToEstimatedDate?: number; // Resolve field
  clientId: number;
  client: Client;
  status: DeliveryStatus;
  type: DeliveryType;
  minPrice?: number;
  maxPrice?: number;
  medianPrice?: number;
  twoWayDeliverType?: TwoWayDelivery;
  carAvailabilityDate: Date | undefined;
  carPickUpDate: Date | undefined;
  carDeliveryDeadline: Date | undefined;
  courtoiseReturnDate?: Date;
  numberOfVehicles: number;
  optionNameFilter?: string;
  optionIdFilter?: string;
  selectedOptions: DeliveryOptionSelection[];
  duration: number;
  distance: number;
  startCode: string;
  destinationCode: string;
  fromAddressId?: string;
  fromAddress?: Address;
  toReturnFromAddress?: Address;
  fromAddressPrecision?: string;
  toAddressId?: string;
  toAddress?: Address;
  toReturnToAddress?: Address;
  toAddressPrecision?: string;
  wantedPrice?: number;
  finalPrice?: number;
  clientInitialPrice?: number;
  clientFinalPrice?: number;
  comment?: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientPhone: string;
  clientSiret: string;
  clientCompany?: string | undefined;
  clientDrivingLicenceNumber?: string | undefined;
  representativeFirstName: string;
  representativeLastName: string;
  representativeEmail: string;
  representativePhone: string;
  representativeSiret: string;
  representativeCompany: string;
  vehicles: Vehicle[];
  offers: Offer[];
  publicId: string; // Resolve field
  isEditable?: boolean; // ? resolve field
  createdAt: Date;
  updatedAt?: Date;
  departedAt?: Date;
  completedAt?: Date;
  confirmedAt?: Date;
  putInAuctionAt?: Date;
  toRetrieveVehicle?: Vehicle;
  subscriptions: Subscription[];
  number_of_offers: number;
  acceptedOffer?: Offer; // Resolve field
  minOfferPrice?: number; // Resolve field
  averageOffersPrice?: number; // Resolve field
  // outfitRating?: number
  // justiceRating?: number
  // correspondenceRating?: number
  // takeOffRating?: number
  // qualityRating?: number
  // communicationRating?: number
  rated: boolean;
  isPaid: boolean;
  latestLocation?: Coords;
  confirmedByAdminId?: number;
  confirmedByAdmin?: Admin;
  selectedDriverId?: number;
  selectedDriver?: Driver;
  smsId?: string;
  smsSentAt?: Date;
  confirmedByCustomerAt?: Date;
  remainingDistance?: number;
  payment?: DeliveryPayment;
  category: "STANDARD" | "EXPRESS" | "OKM";
  docs: Doc[];
  validationDocs: Doc[];
  organization: Organization;
  deliveryOrganization: Organization;
  organizationId: number;
  invoice: string;
  selectedOffer: Offer;
  accepted_offer: Offer;
  cancelledAt?: Date;
  cancelReason?: TCancelDelivery;
};
export type Doc = StoredMediaFile;
export type CreateDeliveryInput = Omit<Delivery, "id" | "client" | "docs"> & {
  docs: string;
} & {
  vehicles?: Vehicle[];
};
export type CalculateDeliveryPriceInput = {
  fromAddress: Address;
  toAddress: Address;
  carPickUpDate: Date | undefined;
  numberOfVehicles: number;
};
export type DeliveryEstimateData = {
  distance: number;
  duration: number;
  clientInitialPrice: number;
  minPrice: number;
  medianPrice: number;
  maxPrice: number;
};
